// App.js
import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { Flex, useDisclosure, IconButton, Avatar,
   DrawerOverlay, DrawerContent, DrawerHeader,
  DrawerBody, DrawerFooter, Button, Box, Text, Heading,
  Tabs, TabList, TabPanels, Tab, TabPanel, SimpleGrid,Drawer
} from '@chakra-ui/react';
import { Loader } from 'lucide-react';

import AuthPage from './components/AuthPage';
import Dashboard from './components/Dashboard';
import BillingPage from './components/BillingPage';
import SuccessPage from './components/SuccessPage';
import CancelPage from './components/CancelPage';
import Landing from './pages/Landing';


const UserDrawer = ({ isOpen, onClose, user, handleLogout }) => (
  <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
    <DrawerOverlay />
    <DrawerContent>
      <DrawerHeader borderBottomWidth="1px">Profilo Utente</DrawerHeader>
      <DrawerBody>
        <Tabs variant="enclosed-colored" colorScheme="indigo">
          <TabList>
            <Tab>Info</Tab>
            <Tab>Storico</Tab>
            <Tab>Impostazioni</Tab>
          </TabList>

          <TabPanels>
            {/* Info account */}
            <TabPanel>
              <Box mb={4}>
                <Heading as="h3" size="md">{user?.userName || user?.email}</Heading>
                <Text fontSize="sm" color="gray.500">{user?.email}</Text>
                <Text fontSize="sm" color="gray.500">Piano: {user?.subscriptionPlan || '—'}</Text>
                <Text fontSize="sm" color="gray.500">Crediti: {user?.remainingCredits ?? '—'}</Text>
              </Box>
            </TabPanel>

            {/* Storico conversioni */}
            <TabPanel>
              {user?.conversionHistory?.length ? (
                <SimpleGrid columns={1} spacing={3}>
                  {user.conversionHistory.map((conv, idx) => (
                    <Box key={idx} p={3} border="1px" borderColor="gray.200" borderRadius="md">
                      <Text fontSize="xs" color="gray.600">
                        {new Date(conv.date).toLocaleString()}
                      </Text>
                      <Text fontSize="sm">File: {conv.files}</Text>
                      <Text fontSize="sm">Pacchetto: {conv.zipFile}</Text>
                    </Box>
                  ))}
                </SimpleGrid>
              ) : (
                <Text fontSize="sm" color="gray.500">Nessuna conversione registrata.</Text>
              )}
            </TabPanel>

            {/* Impostazioni */}
            <TabPanel>
              <Text fontSize="sm" color="gray.600">Qui puoi aggiungere preferenze, notifiche, ecc.</Text>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </DrawerBody>
      <DrawerFooter>
        <Button variant="outline" mr={3} onClick={onClose}>Chiudi</Button>
        <Button colorScheme="red" onClick={handleLogout}>Logout</Button>
      </DrawerFooter>
    </DrawerContent>
  </Drawer>
);

const Navbar = ({ onOpenProfile, user }) => (
  <Flex bg="indigo.600" p={4} justify="space-between" align="center">
    <Flex gap={6}>
      <Button as={Link} to="/" variant="ghost" color="white" _hover={{ bg: "indigo.700" }}>
        Dashboard
      </Button>
      <Button as={Link} to="/billing" variant="ghost" color="white" _hover={{ bg: "indigo.700" }}>
        Billing
      </Button>
    </Flex>
    {/* Avatar tondo come bottone */}
    <IconButton
      onClick={onOpenProfile}
      variant="ghost"
      borderRadius="full"
      aria-label="Profilo"
      icon={
        <Avatar
          size="sm"
          name={user?.userName || user?.email}
          src={user?.avatarUrl} // se hai un campo avatar, altrimenti mostra iniziali
        />
      }
    />
  </Flex>
);

const App = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchUser = useCallback(async () => {
    try {
      if (!token) return;
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Errore fetch user");
      const data = await res.json();
      setUser(data);
      localStorage.setItem("user", JSON.stringify(data));
    } catch (err) {
      console.error("Errore caricamento utente:", err);
    }
  }, [token]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
      setToken(storedToken);
      fetchUser();
    }
    setIsAuthLoading(false);
  }, []);

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    onClose();
  };

  if (isAuthLoading) {
    return (
      <Flex align="center" justify="center" minH="100vh" bg="gray.50">
        <Loader size={32} color="indigo.600" className="animate-spin" style={{ marginRight: '12px' }} />
        <Text fontSize="xl" color="indigo.700">Caricamento in corso...</Text>
      </Flex>
    );
  }

  // Non autenticato: Landing + Auth
  if (!user || !token) {
    return (
      <Router>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<AuthPage setUser={setUser} setToken={setToken} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    );
  }

  // Autenticato: Navbar + Drawer + rotte interne
  return (
    <Router>
      <Navbar onOpenProfile={onOpen} />
      <UserDrawer isOpen={isOpen} onClose={onClose} user={user} handleLogout={handleLogout} />

      <Routes>
        <Route path="/" element={<Dashboard user={user} setUser={setUser} handleLogout={handleLogout} />} />
        <Route path="/billing" element={<BillingPage user={user} />} />
        <Route path="/success" element={<SuccessPage fetchUser={fetchUser} />} />
        <Route path="/cancel" element={<CancelPage />} />
        {/* Opzionale: rotta /profile, ma il profilo si apre dal drawer */}
        <Route path="/profile" element={<Flex p={6}><Text>Apri il profilo dal menu in alto.</Text></Flex>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

export default App;
